// src/cockpit4/components/FinanceReconciliation.tsx
// ============================================
// P0 算账关：客户/成交单对账看板（FDE 补全）
//
// 目标：让老板直接看到"每一笔钱对到哪个客户 / 哪张成交单"，回答"这真账对得上吗"。
//   - 数据源：XEAOS/Finance/transactions.json（Vault，财务唯一真相源）+ localStorage 兜底
//   - 按 customer → deal_id 分组归类收入，标注 settled(已回款/待到账)
//   - 提供"同步到服务端账本(/finance-api)"：把流水 upsert 成可检索的逐记录账目，
//     使后端 /finance-api 按 customer/dealId 可查，老板可服务端对账
//
// 兼容：只读 + 显式按钮同步，不改变既有财务组件行为（非破坏性）。
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  RefreshCw,
  FileText,
  UploadCloud,
  CheckCircle2,
  Clock,
  Database,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
} from "lucide-react";
import { loadFinanceJson } from "../../services/financeVaultStorage";

// ============================================
// 类型（与 TransactionList 数据源保持一致）
// ============================================

interface ReconTx {
  id: string;
  date: string; // YYYY-MM-DD
  type: "income" | "expense";
  counterparty?: string;
  amount: number;
  category?: string;
  note?: string;
  source?: "auto" | "manual";
  deal_id?: string; // 关联 CRM 付款单号（记账凭证号）
  customer?: string; // 归因客户名
  settled?: boolean; // 是否已回款确认
}

const STORAGE_KEY = "xeaos_finance_transactions";
const VAULT_FILE = "transactions.json";

function fmtMoney(v: number): string {
  const n = Number(v) || 0;
  if (n >= 10000) return `¥ ${(n / 10000).toFixed(2)} 万`;
  return `¥ ${n.toLocaleString()}`;
}

// ============================================
// 主组件
// ============================================

export default function FinanceReconciliation() {
  const [txs, setTxs] = useState<ReconTx[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [lastRefresh, setLastRefresh] = useState("");

  /** 从 Vault（优先）/ localStorage（兜底）读取流水 */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadFinanceJson<ReconTx[]>(VAULT_FILE, STORAGE_KEY, []);
      setTxs(Array.isArray(data) ? data : []);
      setLastRefresh(new Date().toLocaleString("zh-CN"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** 只保留带了成交/客户归因的流水，才是"可对账"的记录 */
  const attributable = useMemo(
    () => txs.filter((t) => t && (t.deal_id || t.customer)),
    [txs]
  );

  /** 按 客户 → 成交单 分组的收入回款汇总 */
  const byCustomerDeal = useMemo(() => {
    const map = new Map<string, {
      customer: string;
      deals: Map<string, { dealId: string; income: number; settled: number; pending: number }>;
      income: number;
      settled: number;
      pending: number;
    }>();

    for (const t of attributable) {
      if (t.type !== "income") continue;
      const customer = t.customer || "（未命名客户）";
      const dealId = t.deal_id || "—";
      if (!map.has(customer)) {
        map.set(customer, { customer, deals: new Map(), income: 0, settled: 0, pending: 0 });
      }
      const c = map.get(customer)!;
      if (!c.deals.has(dealId)) {
        c.deals.set(dealId, { dealId, income: 0, settled: 0, pending: 0 });
      }
      const d = c.deals.get(dealId)!;
      const amt = Number(t.amount) || 0;
      c.income += amt;
      d.income += amt;
      if (t.settled) {
        c.settled += amt;
        d.settled += amt;
      } else {
        c.pending += amt;
        d.pending += amt;
      }
    }
    return [...map.values()];
  }, [attributable]);

  const totals = useMemo(() => {
    let income = 0;
    let settled = 0;
    let pending = 0;
    for (const d of attributable) {
      if (d.type !== "income") continue;
      income += Number(d.amount) || 0;
      if (d.settled) settled += Number(d.amount) || 0;
      else pending += Number(d.amount) || 0;
    }
    return { income, settled, pending, deals: byCustomerDeal.reduce((n, c) => n + c.deals.size, 0) };
  }, [attributable, byCustomerDeal]);

  /** 幂等同步：把可归因流水 upsert 进 /finance-api 服务端账本（超管可写） */
  const syncToLedger = useCallback(async () => {
    setSyncing(true);
    setSyncMsg("");
    let ok = 0;
    let fail = 0;
    try {
      for (const t of attributable) {
        try {
          const res = await fetch("/finance-api", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: `tx-${t.id || `${t.date}-${t.counterparty ?? ""}-${t.amount}`}`,
              amount: Math.abs(Number(t.amount) || 0),
              kind: t.type === "income" ? "in" : "out",
              category: t.category || "其他",
              customerId: t.customer?.trim() ? t.customer.trim() : "",
              dealId: t.deal_id?.trim() ? t.deal_id.trim() : "",
              date: t.date || new Date().toISOString().slice(0, 10),
              note: t.note || t.counterparty || "",
            }),
          });
          if (res.ok) ok += 1;
          else fail += 1;
        } catch {
          fail += 1;
        }
      }
      setSyncMsg(
        fail === 0
          ? `已同步 ${ok} 笔到服务端账本 /finance-api（按客户/成交单可查）`
          : `同步完成：成功 ${ok} 笔，失败 ${fail} 笔（可能无权限，未登录或非超管）`
      );
    } finally {
      setSyncing(false);
    }
  }, [attributable]);

  // ============================================
  // 渲染
  // ============================================

  return (
    <div className="space-y-3">
      {/* 顶部操作栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-500">
            对账数据源：Vault 流水（transactions.json）· 按 客户→成交单 归集
          </span>
          {lastRefresh && (
            <span className="text-[9px] font-mono text-gray-600">刷新于 {lastRefresh}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => void refresh()}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition disabled:opacity-50"
          >
            <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
            {loading ? "读取中" : "刷新"}
          </button>
          <button
            onClick={() => void syncToLedger()}
            disabled={syncing || attributable.length === 0}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 transition disabled:opacity-50"
          >
            <UploadCloud size={10} className={syncing ? "animate-pulse" : ""} />
            {syncing ? "同步中" : "同步到账本(/finance-api)"}
          </button>
        </div>
      </div>

      {syncMsg && (
        <div className="px-4 py-2 rounded-md border border-cyan-500/20 bg-cyan-500/[0.03] text-[10px] font-mono text-cyan-300/80">
          {syncMsg}
        </div>
      )}

      {/* 汇总卡片 */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "可归因收入", value: fmtMoney(totals.income), color: "#10B981", icon: ArrowUpRight },
          { label: "已回款(sent)", value: fmtMoney(totals.settled), color: "#06B6D4", icon: CheckCircle2 },
          { label: "待到账(pending)", value: fmtMoney(totals.pending), color: "#F59E0B", icon: Clock },
          { label: "涉及成交单", value: String(totals.deals), color: "#A855F7", icon: Wallet },
        ].map((c, idx) => {
          const Icon = c.icon;
          return (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.05 }}
              className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 overflow-hidden"
            >
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent, ${c.color}80, transparent)` }}
              />
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={11} style={{ color: c.color }} />
                <span className="text-[9px] font-mono text-gray-500">{c.label}</span>
              </div>
              <div className="text-[16px] font-mono font-bold text-white">{c.value}</div>
            </motion.div>
          );
        })}
      </div>

      {/* 按客户→成交单归集（对账主体） */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06] bg-white/[0.015]">
          <div className="flex items-center gap-2">
            <FileText size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              客户 · 成交单对账
            </span>
          </div>
          <span className="text-[9px] font-mono text-gray-600">
            可对账流水 {attributable.length} 笔
          </span>
        </div>

        {byCustomerDeal.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Database size={22} className="text-gray-700 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">暂无带客户/成交单的流水</p>
            <p className="text-[9px] font-mono text-gray-600 mt-1 max-w-sm">
              收支明细中关联了客户与成交单的流水才会在此归集。先把交易关联成交单，再回来对账。
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {byCustomerDeal.map((c) => (
              <div key={c.customer} className="px-3 py-2">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono text-white">{c.customer}</span>
                    <span className="text-[9px] font-mono text-gray-500">
                      {c.deals.size} 张成交单
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-mono">
                    <span className="text-gray-400">已回款 {fmtMoney(c.settled)}</span>
                    {c.pending > 0 && <span className="text-amber-300">待到账 {fmtMoney(c.pending)}</span>}
                    <span className="text-emerald-300">{fmtMoney(c.income)}</span>
                  </div>
                </div>
                <div className="space-y-1 pl-1 border-l border-white/[0.04] ml-1">
                  {[...c.deals.values()].map((d) => (
                    <div
                      key={d.dealId}
                      className="flex items-center justify-between px-2 py-1 text-[10px] font-mono rounded bg-white/[0.01]"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">成交单</span>
                        <span className="text-gray-300">{d.dealId}</span>
                        {d.pending > 0 ? (
                          <span className="flex items-center gap-0.5 text-amber-300">
                            <Clock size={9} /> 待到账
                          </span>
                        ) : (
                          <span className="flex items-center gap-0.5 text-emerald-400">
                            <CheckCircle2 size={9} /> 已回款
                          </span>
                        )}
                      </div>
                      <span className="text-emerald-300">{fmtMoney(d.income)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 底部说明 */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-[9px] font-mono text-gray-600">
          <ArrowDownRight size={9} className="inline mr-1" />
          P0 算账关：对不上账的交付都是自我感动。同步到账本后，老板可在服务端按客户/成交单检索回款。
        </span>
      </div>
    </div>
  );
}